import { useMutation, useQuery } from '@apollo/client'
import Box from '@components/base/Box'
import LoadingSpinner from '@components/base/LoadingSpinner'
import Modal from '@components/base/Modal'
import { EmailTemplateListItem } from '@components/email-builder/emailbuilder.type'
import { GET_MEMBERSHIP_FOR_ORGANISATION } from '@graphql/organisation/getMembershipOptionsForOrganisation'
import { useAuth } from '@libs/Auth/useAuth'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { CREATE_MEMBERSHIP } from '@graphql/organisation/createMebership'
import EmailBuilderUI, {
  clearEmailContentCache,
} from '@components/email-builder'
import { FormError } from '@libs/CreateEventContext'
import { Button } from '@components/inputs/Button'
import {
  CreateMembershipInput,
  CreateMembershipOutput,
  EventEmailTemplate,
  EventEmailTrigger,
  ProductPriceInput,
  UpdateMembershipInput,
} from '@graphql/__generated/graphql'
import { TextInput } from '@components/inputs/TextInput'
import { FileInput } from '@components/inputs/FileInput'
import useFileUploader from '@libs/useFileUploader'
import RichTextEditor from '@components/inputs/RichTextEditor/RichTextEditor'
import Accordion from '@components/event/Accordion'
import Table from '@components/base/Table'
import moment from 'moment'
import TableRowActions from '@components/event/TableRowActions'
import { UPDATE_MEMBERSHIP } from '@graphql/organisation/updateMebership'
import { PriceInput } from '@components/inputs/PriceInput'
import currencies from '@libs/currencies'
import { useOrganisationProfile } from '@libs/useOrganisationProfile'
import { DropdownInput } from '@components/inputs/DropdownInput'
import { cleanGqlPayload, debounce } from '@libs/Utility/util'
import toast, { Toaster } from 'react-hot-toast'
import { EditorMembershipCustomDecorators } from '@components/inputs/RichTextEditor/TextEditorUtil'
import { TRIGGER_EVENT_EMAIL } from '@graphql/events/triggerEmailEvent'
import { EmailPreview } from '@components/email-builder/emailPreview'
import { TRIGGER_MEMBERSHIP_EMAIL } from '@graphql/membership/triggerMembershipMail'
import { Tab, Tabs } from '@components/base/Tabs'
import { CheckboxInput } from '@components/inputs/CheckboxInput'
import useForummRouter, {
  ForummRouterEvents,
} from '@libs/Router/useForummRouter'
import { useRouter } from 'next/router'
import { useOrganisationContext } from '@libs/Router/OrganisationContext'

const INITIAL_MEMBERSHIP_DATA: Partial<UpdateMembershipInput> = {
  title: 'Premium Membership',
  shortDescription: 'A brief description of what your membership offers.',
  // description: undefined,
  thumbnailImage: '',
  bannerImage: '',
  emailTemplates: [],
}

// const INITIAL_PRICE_DATA: CreateMembershipInput = {
//   name: '',
//   organisationName: '',
//   prices: [{ priceAmount: 10000, currency: 'GBP', interval: 'month' }],
// }

type PriceListItem = ProductPriceInput & {
  editIndex?: number
}

export const MembershipSettings = () => {
  const uploadFile = useFileUploader()
  const router = useRouter()
  const saveEnabled = useRef(false)
  const { profile, isOrganizer, isAdmin } = useAuth()
  const { profile: orgProfile } = useOrganisationProfile(profile?.company!)

  const [errors, setErrors] = useState<FormError[]>([])

  const [hasUnsavedProductChanges, setHasUnsavedProductChanges] =
    useState(false)
  const [showPriceModal, setShowPriceModal] = useState(false)
  const [priceModalData, setPriceModalData] = useState<PriceListItem>()
  const [productData, setProductData] = useState<CreateMembershipInput>()

  const [showEmailModal, setShowEmailModal] = useState(false)
  const [emailModalData, setEmailModalData] = useState<EmailTemplateListItem>(
    {}
  )

  const [descriptionData, setDescriptionData] = useState<string | undefined>(
    undefined
  )
  const [createMembership] = useMutation(CREATE_MEMBERSHIP)
  const [updateMembership] = useMutation(UPDATE_MEMBERSHIP)
  const [triggerMail] = useMutation(TRIGGER_MEMBERSHIP_EMAIL)

  const { setHasUnsavedChanges } = useOrganisationContext()

  const { data, loading, error, refetch } = useQuery(
    GET_MEMBERSHIP_FOR_ORGANISATION,
    {
      variables: { data: profile?.company! },
      skip: !profile?.company || !isOrganizer,
    }
  )
  const [membershipData, setMembershipData] =
    useState<Partial<UpdateMembershipInput>>()

  const [isLoading, setIsLoading] = useState<boolean>(true)

  useEffect(() => {
    // console.log({ data, loading, error })
    if (data?.getMembershipForOrganisation.__typename === 'APIError') {
      errorHandler(data.getMembershipForOrganisation.code)(
        data.getMembershipForOrganisation.error,
        data.getMembershipForOrganisation.code
      )
      setIsLoading(false)
      saveEnabled.current = true
    }
    if (
      data &&
      data.getMembershipForOrganisation.__typename === 'GetMembershipOutput'
    ) {
      const {
        title,
        thumbnailImage,
        bannerImage,
        description,
        shortDescription,
        emailTemplates,
        isPublished,
        confirmationText,
      } = data.getMembershipForOrganisation.details
      // console.log({ description })
      setMembershipData({
        title,
        thumbnailImage,
        bannerImage,
        description,
        shortDescription,
        emailTemplates,
        isPublished,
        confirmationText,
      })
      setDescriptionData(description ?? 'Membership Description')
      const { name, prices } = data.getMembershipForOrganisation.product
      setProductData({
        prices: prices.map((m) => ({ ...m, priceAmount: m.priceAmount / 100 })),
        name: name ?? title,
        organisationName: profile?.company!,
      })
      setIsLoading(false)
      saveEnabled.current = true
    }
  }, [data, loading, error, profile])

  const errorHandler =
    (id: string, alert: boolean = false) =>
    (message: string, testId: string) => {
      const payload = [...errors]

      const hasError = !!message || message !== ''
      const isCleared = payload.find((f) => f.eleId === testId) && !hasError

      if (isCleared) return setErrors(payload.filter((f) => f.eleId !== testId))

      const isDuplicate = !!payload.find((p) => p.eleId === testId)

      const newError = {
        step: id ?? 'unknown',
        message,
        eleId: testId,
        alert,
      }

      if (!isDuplicate && hasError) setErrors([...payload, newError])
    }

  const handleCreateProduct = async () => {
    // console.log({ productData })
    if (!!productData) {
      const { prices, organisationName, name } = productData
      const res = await createMembership({
        variables: {
          data: {
            organisationName: organisationName ?? orgProfile.name,
            prices: prices.map((m) => ({
              priceAmount: m.priceAmount * 100,
              interval: m.interval,
              currency: m.currency,
              title: m.title,
              pillText: m.pillText,
              priceId: m.priceId,
            })),
            name:
              name ??
              membershipData?.title ??
              `${orgProfile.name}'s Membership`,
          },
        },
      })
        .then((d) => {
          setProductData((state) => {
            const res =
              d.data?.createMembership.__typename === 'CreateMembershipOutput'
                ? (d.data.createMembership as CreateMembershipOutput).prices
                : (state?.prices ?? [])

            const payload = {
              ...state,
              prices: prices.map((p) => ({
                ...p,
                priceId: res.find((f) =>
                  !!p.priceId ? f.priceId === p.priceId : f.title === p.title
                )?.priceId,
              })),
            }

            return payload as CreateMembershipInput
          })
        })
        .then(() => {
          setHasUnsavedProductChanges(false)
          setHasUnsavedChanges(false)
        })
    }
  }

  const saveForm = useCallback(
    debounce((payload: UpdateMembershipInput) => {
      if (payload)
        updateMembership({
          variables: { data: payload },
        }).then((res) => {
          if (res.data?.updateMembership.__typename !== 'APIError') {
            if (res.data?.updateMembership.success)
              toast(`Saved`, {
                icon: '✔ ',
                duration: 1000,
                className: 'custom-toast',
              })
            else {
              toast(`Save Issue`, {
                icon: '⚠ ',
                duration: 3000,
                className: 'custom-toast',
              })

              toast(`Saved data to clipboard.`, {
                icon: '⚠ ',
                duration: 8000,
                className: 'custom-toast',
              })
              window.navigator.clipboard.writeText(
                JSON.stringify(payload, null, 2)
              )
            }
          }
        })
    }, 1000),
    []
  )

  useEffect(() => {
    const payload: UpdateMembershipInput = cleanGqlPayload({
      title: membershipData?.title,
      description: membershipData?.description,
      thumbnailImage: membershipData?.thumbnailImage,
      bannerImage: membershipData?.bannerImage,
      shortDescription: membershipData?.shortDescription,
      confirmationText: membershipData?.confirmationText,
      emailTemplates: membershipData?.emailTemplates,
      // emailTemplates: [
      //   ...(membershipData?.emailTemplates ?? []),
      //   {
      //     title: emailModalData.title,
      //     data: emailModalData.data,
      //     content: emailModalData.content,
      //     enabled: emailModalData.enabled,
      //     trigger: emailModalData.trigger,
      //   },
      // ],
    }) as UpdateMembershipInput
    // console.log({ payload, saveEnabled: saveEnabled.current })
    if (payload && saveEnabled.current) saveForm(payload)
  }, [membershipData, saveEnabled, saveForm])

  //
  //
  //INFO Modals

  const emailBuilderModal = useMemo(
    () => (
      <Modal
        show={showEmailModal}
        setShow={setShowEmailModal}
        title={`${
          emailModalData?.editIndex !== undefined ||
          emailModalData.editIndex === 0
            ? 'Edit'
            : 'Create'
        } Email`}
        className={`flex overflow-visible w-full flex-col justify-between`}
      >
        <EmailBuilderUI
          onSubmit={(data) => {
            // console.log({ data })
            setEmailModalData(data)
          }}
          enablePreview
          setError={errorHandler('email_builder')}
          template={emailModalData}
          decorators={EditorMembershipCustomDecorators}
          data={membershipData}
          type={'membership'}
          cacheScopeId={profile?.company}
        />
        <Button
          className="mb-4 ml-auto"
          title={`Save Email`}
          type="primary"
          onClick={(e) => {
            e.preventDefault()
            // console.log({
            //   templates: membershipData?.emailTemplates,
            //   emailModalData,
            // })

            let payload: Partial<UpdateMembershipInput> = {
              ...membershipData,
            }

            const hasRegistrationConfirmationTemplate =
              !!payload?.emailTemplates?.find(
                (f) => f.trigger === EventEmailTrigger.Confirmation
              )

            const newEmail: EventEmailTemplate = {
              title: emailModalData.title,
              enabled: true,
              trigger: emailModalData.trigger,
              data: emailModalData.data,
              content: emailModalData.content,
            }

            if (!payload.emailTemplates) payload.emailTemplates = []

            if (
              newEmail.trigger === EventEmailTrigger.Confirmation &&
              hasRegistrationConfirmationTemplate
            )
              payload = {
                ...payload,
                emailTemplates: [
                  ...payload.emailTemplates.map((m) => ({
                    title: m.title,
                    content: m.content,
                    data: m.data,
                    enabled: m.enabled,
                    trigger: EventEmailTrigger.Manual,
                  })),
                ],
              }

            if (!!payload.emailTemplates) {
              if (emailModalData.editIndex === 0 || emailModalData.editIndex)
                payload.emailTemplates = payload.emailTemplates.map((m, i) => {
                  if (emailModalData.editIndex === i) return newEmail
                  return m
                })
              else
                payload.emailTemplates = [...payload.emailTemplates, newEmail]
            }

            console.log({ payload })
            setMembershipData(payload)
            clearEmailContentCache({
              type: 'membership',
              scopeId: profile?.company,
              template: emailModalData,
            })
            setShowEmailModal(false)
            setEmailModalData({})
          }}
        />
      </Modal>
    ),
    [membershipData, showEmailModal, emailModalData]
  )

  const [actionLoading, setActionLoading] = useState(false)

  const priceModal = useMemo(
    () => (
      <Modal
        show={showPriceModal}
        setShow={setShowPriceModal}
        title={`${
          priceModalData?.editIndex !== undefined ||
          priceModalData?.editIndex === 0
            ? 'Edit'
            : 'Create'
        } Price`}
        className={`flex overflow-visible w-full flex-col justify-between`}
      >
        <TextInput
          tooltip="Price Option Title"
          testid={'name-input'}
          validations={{
            maxLength: {
              value: 30,
              message: 'Title must be 30 characters or less',
            },
          }}
          hint="(Maximum of 30 characters)"
          label="Title"
          className="flex-1 mr-2"
          value={priceModalData?.title}
          onChange={(data) => {
            setPriceModalData(
              (state) =>
                ({
                  ...state,
                  title: data,
                }) as PriceListItem
            )
          }}
        />

        <TextInput
          tooltip="Price Pill Title"
          testid={'price-pill-input'}
          validations={{
            maxLength: {
              value: 15,
              message: 'Title must be 15 characters or less',
            },
          }}
          hint="What shows on the membership pricing option inside of the colored pill shaped section."
          label="Pill Text"
          className="flex-1 mr-2"
          value={priceModalData?.pillText}
          onChange={(data) => {
            setPriceModalData(
              (state) =>
                ({
                  ...state,
                  pillText: data,
                }) as PriceListItem
            )
          }}
        />
        <PriceInput
          hint="Amount"
          className={`w-full`}
          currencySymbol={
            currencies[orgProfile.currency ?? 'GBP'].symbol as any
          }
          onChange={(data) => {
            setPriceModalData(
              (state) => ({ ...state, priceAmount: data }) as PriceListItem
            )
          }}
          value={priceModalData?.priceAmount ?? 0}
        />

        <DropdownInput
          value={priceModalData?.interval}
          onChange={(data) =>
            setPriceModalData(
              (state) => ({ ...state, interval: data }) as PriceListItem
            )
          }
          options={[
            { label: 'Monthly', value: 'month' },
            { label: 'Annually', value: 'year' },
          ]}
          label="Select a payment interval"
          className="w-full sm:w-1/2 lg:w-1/4 "
        />
        <Button
          className="mb-4 ml-auto"
          title={`Save Price`}
          type="primary"
          onClick={(e) => {
            e.preventDefault()

            const pricePayload: ProductPriceInput = {
              priceAmount: priceModalData?.priceAmount ?? 10,
              currency: orgProfile.currency,
              interval: priceModalData?.interval ?? 'month',
              title: priceModalData?.title ?? 'Membership',
              pillText: priceModalData?.pillText,
            }

            let payload = {
              ...productData,
              prices: productData?.prices ?? [],
            }

            if (!!priceModalData) {
              if (priceModalData.editIndex === 0 || priceModalData?.editIndex)
                payload.prices[priceModalData.editIndex] = pricePayload
              else payload.prices.push(pricePayload)
            }
            // console.log({ payload })
            // setMembershipData(payload)

            setProductData(payload as CreateMembershipInput)
            setShowPriceModal(false)
            setHasUnsavedProductChanges(true)
            setHasUnsavedChanges(true)
            setPriceModalData({} as PriceListItem)
          }}
        />
      </Modal>
    ),
    [productData, showPriceModal, priceModalData]
  )
  const [tab, setTab] = useState('form')
  const [showCompletedPreview, setShowCompletedPreview] = useState(false)

  if (isLoading)
    return <LoadingSpinner className="mt-1 mx-auto" size="medium" />

  return (
    <>
      <Tabs activeTitle={tab}>
        <Tab title="form">
          {emailBuilderModal}
          {priceModal}
          {/* {errors && ( */}
          {/*   <Box */}
          {/*     ignoreTheme */}
          {/*     className={`mt-2 text-center text-red-500 text-md pb-4`} */}
          {/*   > */}
          {/*     {errors.map((e) => `${e.message}`)}{' '} */}
          {/*   </Box> */}
          {/* )} */}
          <Button
            confirmationModal={{
              title: 'Confirm Publish Membership',
              content:
                'Are you sure you want to publish this membership? This cannot be undone.',
            }}
            onClick={() => {
              if (isAdmin || isOrganizer) {
                updateMembership({
                  variables: {
                    data: {
                      isPublished: true,
                    },
                  },
                }).then(() => refetch())
              }
            }}
            title={
              membershipData?.isPublished ? 'Published' : 'Publish Membership'
            }
            type="success"
            size="medium"
            disabled={membershipData?.isPublished ?? false}
            className="ml-auto mr-4 min-w-[110px]"
            isInConfirmationModal={true}
          />
          <Accordion
            className={'mt-6'}
            title={'Membership Details'}
            initiallyOpen={false}
            key={'membership_details_section'}
            error={
              errors?.find((e) => e.step === 'INVALID_MEMBERSHIP_DETAILS')
                ?.message
            }
            show={true}
          >
            <Box className="flex flex-col items-center mt-14 text-white w-full">
              <Box className="flex w-full space-x-12">
                <Box className="w-full">
                  <Box className={`flex flex-col w-full -mb-2`}>
                    <TextInput
                      tooltip="Event title is required to publish"
                      testid={'name-input'}
                      validations={{
                        maxLength: {
                          value: 150,
                          message: 'Title must be 150 characters or less',
                        },
                      }}
                      hint="(Maximum of 150 characters)"
                      label="Title"
                      className="flex-1 mr-2"
                      value={membershipData?.title}
                      onChange={(data) => {
                        setMembershipData((state) => ({
                          ...state,
                          title: data,
                        }))
                      }}
                    />
                    <TextInput
                      tooltip="A short event description is required to publish"
                      testid={'short-description-input'}
                      validations={{
                        maxLength: {
                          value: 300,
                          message: 'Description must be 300 characters or less',
                        },
                      }}
                      setError={errorHandler('event')}
                      hint="(Maximum of 300 characters)"
                      label="Short Description"
                      className="flex-1 ml-2 sm:ml-0 mt-1"
                      value={membershipData?.shortDescription}
                      onChange={(data) => {
                        setMembershipData((state) => ({
                          ...(state ?? {}),
                          shortDescription: data,
                        }))
                      }}
                    />
                  </Box>
                  <RichTextEditor
                    key={'membership_rich_editor_descripiton'}
                    tooltip="A full description of your membership programme."
                    testid="description-input"
                    className="mt-2"
                    setError={errorHandler('event')}
                    label="Description"
                    placeholder={`Write all of the details of your membership...`}
                    value={membershipData?.description}
                    onChange={(jsonString) => {
                      if (membershipData?.description !== jsonString)
                        setMembershipData((state) => ({
                          ...state,
                          description: jsonString,
                        }))
                      // setDescriptionData(jsonString)
                    }}
                  />

                  <RichTextEditor
                    key={'membership_rich_editor_confirmation'}
                    tooltip="The text that appears on the confirmation screen after a membership is purchased."
                    testid="confirmation-text-input"
                    className="mt-2"
                    setError={errorHandler('event')}
                    label="Confirmation Text"
                    placeholder={`Write the text displayed on the membership confirmation screen`}
                    value={membershipData?.confirmationText}
                    onChange={(jsonString) => {
                      if (membershipData?.confirmationText !== jsonString)
                        setMembershipData((state) => ({
                          ...state,
                          confirmationText: jsonString,
                        }))
                      // setDescriptionData(jsonString)
                    }}
                  />
                  <Box className="flex flex-wrap mt-2">
                    <FileInput
                      uploadFile={uploadFile}
                      label="Banner Image"
                      hint="recommended size (1200x400)."
                      crop={true}
                      cropAspectRatio={4}
                      cropShape={'rect'}
                      testid={'banner-upload'}
                      value={membershipData?.bannerImage!}
                      onChange={(data) => {
                        setMembershipData((state) => ({
                          ...state,
                          bannerImage: data as string,
                        }))
                      }}
                    />

                    <FileInput
                      uploadFile={uploadFile}
                      label="Thumbnail Image"
                      crop={true}
                      hint="recommended size (400x400)."
                      cropAspectRatio={1}
                      cropShape={'rect'}
                      data-testid={'thumbnail-upload'}
                      value={membershipData?.thumbnailImage!}
                      onChange={(data) => {
                        setMembershipData((state) => ({
                          ...state,
                          thumbnailImage: data as string,
                        }))
                      }}
                    />
                  </Box>
                </Box>
              </Box>
            </Box>
          </Accordion>
          <Accordion
            className={'mt-6'}
            title={'Membership Options'}
            initiallyOpen={false}
            error={errors?.find((e) => e.step === 'INVALID_PRODUCT')?.message}
            show={true}
            showSaveButton={hasUnsavedProductChanges}
            onSaveClicked={async () => {
              await handleCreateProduct()
            }}
          >
            <Box className="flex items-center space-x-2 mb-4 mt-6">
              <Box className="text-xl">Purchase Options</Box>
              <Box className="text-lg">
                After changing your membership pricing options, save them by
                clicking the Save button in the top-right corner of this
                section.
              </Box>
              <Button
                onClick={() => {
                  // setEmailModalData({
                  //   title: `Email Template ${membershipData?.?.length ?? 1}`,
                  //   trigger:
                  //     (membershipData?.emailTemplates?.length ?? 0) > 0
                  //       ? EventEmailTrigger.Manual
                  //       : EventEmailTrigger.Confirmation,
                  // })
                  setPriceModalData({} as PriceListItem)
                  setShowPriceModal(true)
                }}
                title="Create purchase option"
                type="secondary"
                size="small"
              />
            </Box>
            <Table
              sortable
              items={productData?.prices ?? undefined}
              onSort={(e) => {
                // console.log({ e })
                const payload = {
                  ...membershipData,
                  emailTemplates: e,
                  // emailTemplates: e.map((m) => {
                  //   try {
                  //     delete m.sentDateTime
                  //   } catch (_) {}
                  //   return m
                  // }),
                }
                setProductData(
                  (state) => ({ ...state, prices: e }) as CreateMembershipInput
                )
              }}
              tableHeading={['Title', 'Interval', 'Price', '']}
              rows={productData?.prices?.map((price, index) => [
                `${price.title}`,
                <Box
                  key={`notification-status-${index}`}
                  className={`rounded-md`}
                >
                  {price.interval.toUpperCase()}
                </Box>,
                `${currencies[orgProfile.currency ?? 'GBP'].symbol}${price.priceAmount}`,
                <TableRowActions
                  key={index}
                  editClicked={() => {
                    // console.log({ price, index })
                    setPriceModalData({
                      ...price,
                      editIndex: index,
                    })
                    setShowPriceModal(true)
                  }}
                  deleteClicked={() => {
                    setProductData(
                      (state) =>
                        ({
                          ...state,
                          prices: state?.prices?.filter((_, i) => i !== index),
                        }) as CreateMembershipInput
                    )
                  }}
                />,
              ])}
            />
            <Box show={productData?.prices?.length === 0}>
              <Box className="text-sm mt-8 text-center mb-8">
                {'No purchase options have been created yet'}
              </Box>
            </Box>
          </Accordion>
          <Accordion
            className={'mt-4'}
            title={'Emails'}
            initiallyOpen={false}
            // error={errors?.find((e) => e.step === 'API_ERROR')?.message}
            show={true}
          >
            <Box className="flex items-center space-x-2 mb-4 mt-6">
              <Box className="text-xl">Membership Emails</Box>
              <Button
                onClick={() => {
                  setEmailModalData({
                    title: `Email Template ${membershipData?.emailTemplates?.length ?? 1}`,
                    trigger:
                      (membershipData?.emailTemplates?.length ?? 0) > 0
                        ? EventEmailTrigger.Manual
                        : EventEmailTrigger.Confirmation,
                  })
                  setShowEmailModal(true)
                }}
                title="Create New Email"
                type="secondary"
                size="small"
              />
            </Box>
            <Table
              sortable
              items={membershipData?.emailTemplates ?? undefined}
              onSort={(e) => {
                // console.log({ e })
                const payload = {
                  ...membershipData,
                  emailTemplates: e,
                  // emailTemplates: e.map((m) => {
                  //   try {
                  //     delete m.sentDateTime
                  //   } catch (_) {}
                  //   return m
                  // }),
                }
                setMembershipData(payload)
              }}
              tableHeading={['Subject', 'Trigger', 'Date Sent', '']}
              rows={membershipData?.emailTemplates?.map(
                (emailTemplates, index) => [
                  emailTemplates?.title?.toUpperCase() ?? `No Title (${index})`,
                  <Box
                    key={`notification-status-${index}`}
                    className={`rounded-md`}
                  >
                    {emailTemplates.trigger}
                  </Box>,
                  emailTemplates.sentDateTime
                    ? moment(emailTemplates.sentDateTime)
                        .format('D MMMM YYYY, h:mm A')
                        .toString()
                    : emailTemplates.trigger === EventEmailTrigger.Manual
                      ? 'Not Sent'
                      : '',
                  <TableRowActions
                    key={index}
                    action={[
                      {
                        title: 'Send',
                        show:
                          emailTemplates.trigger === EventEmailTrigger.Manual,
                        loading: actionLoading,
                        onClicked: () => {
                          if (!!orgProfile.name && !!emailTemplates.title) {
                            setActionLoading(true)
                            triggerMail({
                              variables: {
                                orgName: orgProfile.name,
                                template: emailTemplates.title,
                              },
                            })
                              .then(() => {
                                refetch()
                              })
                              .finally(() => setActionLoading(false))
                          }
                        },
                        confirmModal: {
                          title: 'Are you sure?',
                          content: (
                            <Box className="pt-6 pb-6">
                              <Box className="my-2 mb-6">
                                Are you sure you want to send this email to all
                                attendees? Please ensure your email template is
                                correct before confirming.
                              </Box>
                              <Box className="h-[50vh]">
                                <EmailPreview
                                  resizeEnabled={false}
                                  content={emailTemplates.content!}
                                  type="membership"
                                  data={membershipData}
                                />
                              </Box>
                            </Box>
                          ),
                        },
                      },
                    ]}
                    editClicked={() => {
                      // console.log({ emailTemplates, index })
                      setEmailModalData({
                        ...emailTemplates,
                        editIndex: index,
                      })
                      setShowEmailModal(true)
                    }}
                    deleteClicked={() => {
                      setMembershipData({
                        ...membershipData,
                        emailTemplates: membershipData?.emailTemplates?.filter(
                          (_, i) => i !== index
                        ),
                      })
                    }}
                  />,
                ]
              )}
            />
            <Box show={membershipData?.emailTemplates?.length === 0}>
              <Box className="text-sm mt-8 text-center mb-8">
                {'No event emails have been created yet'}
              </Box>
            </Box>
          </Accordion>

          <Box className="my-4">
            <Button
              className="ml-auto"
              title={`Preview Membership`}
              type="primary"
              onClick={(e) => {
                e.preventDefault()
                setTab('preview')
              }}
            />
          </Box>
        </Tab>
        <Tab title="preview">
          <CheckboxInput
            value={showCompletedPreview}
            onChange={(data) => {
              // console.log({ data })
              setShowCompletedPreview(data)
            }}
            className="bg-transparent rounded text-sm border-box mb-4"
            border=""
            label=""
            customStyles="!justify-start"
          >
            <Box>Show Confirmation Preview</Box>
          </CheckboxInput>
          <Box className="lg:block w-full relative h-full min-h-[65vh] border-forumm-menu-border pb-16">
            <Box
              ignoreTheme
              className="text-md absolute p-2 bg-white rounded text-black opacity-60"
            >
              Preview
            </Box>
            <iframe
              className="w-full h-full rounded border border-white border-solid border-opacity-25 "
              onClick={(e) => {
                e.preventDefault()
              }}
              /* src={`/donation/${formData?.event?.donationUrl}`} */

              src={`/membership/${orgProfile.name}?nav=false&${showCompletedPreview ? 'registered' : 'reset'}`}
            />
            <Box className="mt-2">
              <Button
                className="ml-auto"
                title={'Back to Editor'}
                type="primary"
                onClick={(e) => {
                  e.preventDefault()
                  setTab('form')
                }}
              />
            </Box>
          </Box>
        </Tab>
      </Tabs>
      <Toaster
        toastOptions={{
          className:
            'shadow-lg !rounded-full flex items-center border-2 !bg-forumm-white border-forumm-green !text-forumm-green mt-[80px]',
          duration: 8000,
        }}
      />
    </>
  )
}
